import React, { useCallback, useEffect, useRef, useState } from "react";
import { CsrAPI } from "../../apis/CsrAPI";
import DashboardContent from "../../components/DashboardContent";
import { PERMISSIONS } from "../../utils/permissions";
import { FieldGroup, FormBuilder } from "react-reactive-form";
import StudyDesignQuestions from "../../questions/StudyDesignQuestions";
import useQuestions from "../../hooks/useQuestions";
import useCsr from "../../hooks/useCsr";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createUnifiedSubmitHandler } from "../../utils/unifiedFormSubmit";
import { WIZARD_SECTIONS } from "../../utils/wizardSections";
import {
  getRestorableFormArray,
  getRestoreArrayType,
  toRestorableArray,
} from "../../utils/wizardFormRestore";
import { startStudyDataLoad } from "../../store/singletons/csrDataPollingSingleton";

const isProduction = process.env.NODE_ENV === "production";

const StudyDesignDemographics = () => {
  const { studyId } = useParams();
  const dispatch = useDispatch();
  const { setAjaxLoading } = useCsr();
  const {
    questionsData,
    questionsDataSaved,
    setQuestionsDataSaved,
    loadWizardSections,
    qForm,
    wizardForm,
    setFormFilledData,
    setWizardFormFilledData,
  } = useQuestions();

  const formArraysKeys = {
    qEfficacyAnalytes: "object",
    qPrimaryEfficacyComparisonNames: "object",
    qEfficacyPopulations: "object",
    qEfficacyAnalysisTypeNames: "object",
    qEfficacyTimepointNames: "object",
    qEfficacyKaplanMeierAnalytes: "object",
    qTgNamesDoseFinding: "object",
    qTgNames: "object",
    qTgNamesComparison: "object",
    qPrimaryEfficacyAnalytes: "object",
    qPrimaryEfficacyPopulation: "object",
    qPrimaryEfficacyAnalysisTypeNames: "object",
    qPrimaryEfficacyAnalytesComparison: "object",
    qPrimaryEfficacyBaseline: "object",
    qPrimaryEfficacyTimepointNames: "object",
    qSecondaryEfficacyAnalytes: "object",
    qSubgroupClassification: "object",
    qSingleArmMultipleColumns: "object",
  };

  // Arrays that need blank rows after restore when the saved array is empty.
  // Submit drops rows with any empty field (e.g. a 4b pair whose 4a value was
  // removed, or 4a rows the user cleared), and without rows the question would
  // show only "Add another". `rows` matches the question's minInputs.
  const defaultGroupTemplates = {
    qTgNamesDoseFinding: { fields: ["name"], rows: 2 },
    qTgNames: { fields: ["name", "role"], rows: 2 },
    qPrimaryEfficacyComparisonNames: {
      fields: ["Treatment", "Comparator"],
      rows: 1,
    },
    qTgNamesComparison: { fields: ["Treatment", "Comparator"], rows: 1 },
  };

  const [singleArm, setSingleArm] = useState(null);
  const [multipleArm, setMultipleArm] = useState(null);
  const [doseFinding, setDoseFinding] = useState(null);

  const errRef = useRef();
  const [errMsg, setErrMsg] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [, setRestoreVersion] = useState(0);
  const lastRestoredQuestionsDataRef = useRef(null);

  const selectBranch = useCallback(() => {
    const doseFindingCondition =
      wizardForm.controls.qDoseFinding.value === "true";
    if (doseFindingCondition) {
      setSingleArm(false);
      setMultipleArm(false);
      setDoseFinding(true);
    } else {
      setDoseFinding(false);
      if (wizardForm.controls.qComparative.value === "true") {
        setSingleArm(false);
        setMultipleArm(true);
      } else {
        setSingleArm(true);
        setMultipleArm(false);
      }
    }
  }, [wizardForm]);

  const onChangeIsComparative = useCallback(() => {
    if (isSaving) return;
    selectBranch();
    Object.keys(wizardForm.controls).forEach(controlName => {
      if (
        ![
          "qComparative",
          "qAuthorityLink",
          "qStudyPhase",
          "qStudyPhaseNames",
          "qParticipantsName",
          "qCutOffsForAdverseEventsTables",
          "qCutOffsForDemographicsTables",
          "qCutOffsForAbnormalLabTables",
          "qDemPopulation",
          // Efficacy fields are shared across study types – preserve them
          "qIsEfficacy",
          "qEfficacyNonComp",
          "qEfficacyAnalytes",
          "qPrimaryEfficacyComparisonNames",
          "qEfficacyPopulations",
          "qEfficacyAnalysisTypeNames",
          "qEfficacyBaseline",
          "qEfficacyTimepointNames",
          "qPrimaryEfficacy",
          "qPrimaryEfficacyAnalytes",
          "qPrimaryEfficacyPopulation",
          "qPrimaryEfficacyAnalysisTypeNames",
          "qPrimaryEfficacyAnalytesComparison",
          "qPrimaryEfficacyBaseline",
          "qPrimaryEfficacyTimepointNames",
          "qEfficacyKaplanMeierAnalytes",
          "qSecondaryEfficacyAnalytes",
          "qSubgroupClassification",
          "qSingleArmMultipleColumns",
        ].includes(controlName)
      ) {
        wizardForm.controls[controlName].updateValueAndValidity();
        wizardForm.controls[controlName].reset();
      }
    });
  }, [wizardForm, isSaving, selectBranch]);

  const onChangeDoseFinding = useCallback(() => {
    if (isSaving) return;
    ["qTgNames", "qTgNamesComparison"].forEach(controlName => {
      if (wizardForm.controls[controlName]) {
        wizardForm.controls[controlName].updateValueAndValidity();
        wizardForm.controls[controlName].reset();
      }
    });
  }, [wizardForm, isSaving]);

  const onChangeStudyPhase = useCallback(() => {
    if (isSaving) return;
    if (wizardForm.controls.qStudyPhase.dirty) {
      wizardForm.controls.qStudyPhaseNames.updateValueAndValidity();
      wizardForm.controls.qStudyPhaseNames.reset();
    }
  }, [wizardForm, isSaving]);

  // Initialize branch based on saved form values
  useEffect(() => {
    const doseFindingValue = wizardForm.controls.qDoseFinding.value;
    const comparativeValue = wizardForm.controls.qComparative.value;
    if (doseFindingValue === "true") {
      setSingleArm(false);
      setMultipleArm(false);
      setDoseFinding(true);
    } else if (comparativeValue === "true") {
      setSingleArm(false);
      setMultipleArm(true);
      setDoseFinding(false);
    } else {
      setSingleArm(true);
      setMultipleArm(false);
      setDoseFinding(false);
    }
  }, [
    wizardForm.controls.qDoseFinding.value,
    wizardForm.controls.qComparative.value,
  ]);

  useEffect(() => {
    wizardForm.controls.qComparative.valueChanges.subscribe(() => {
      onChangeIsComparative();
    });
    return () => {
      wizardForm.controls.qComparative.valueChanges.unsubscribe();
    };
  }, [wizardForm.controls.qComparative.value]);

  useEffect(() => {
    wizardForm.controls.qDoseFinding.valueChanges.subscribe(() => {
      selectBranch();
      onChangeDoseFinding();
    });
    return () => {
      wizardForm.controls.qDoseFinding.valueChanges.unsubscribe();
    };
  }, [wizardForm.controls.qDoseFinding.value]);

  useEffect(() => {
    wizardForm.controls.qStudyPhase.valueChanges.subscribe(() => {
      onChangeStudyPhase();
    });
    return () => {
      wizardForm.controls.qStudyPhase.valueChanges.unsubscribe();
    };
  }, [wizardForm.controls.qStudyPhase.value]);

  useEffect(() => {
    wizardForm.controls.qTgNames.valueChanges.subscribe(() => {});
    return () => {
      wizardForm.controls.qTgNames.valueChanges.unsubscribe();
    };
  }, []);

  useEffect(() => {
    // Kick off the single, shared status-polling + data-fetch pipeline for
    // tables and figures. Idempotent — safe across remounts and re-runs.
    if (studyId) {
      dispatch(startStudyDataLoad(studyId));
    }

    setAjaxLoading(true);
    loadWizardSections([WIZARD_SECTIONS.studyDesign])
      .then(() => {
        setErrMsg("");
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else {
          setErrMsg("Failed to get questions");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  }, [studyId, loadWizardSections, setAjaxLoading]);

  useEffect(() => {
    if (!isSaving) {
      retrieveSavedWizardState();
    }
  }, [questionsDataSaved, isSaving]);

  const retrieveSavedWizardState = () => {
    if (isSaving) {
      return;
    }

    if (questionsDataSaved && questionsDataSaved.wizardForm) {
      const wizardFormValue = questionsDataSaved.wizardForm;

      if (lastRestoredQuestionsDataRef.current === questionsDataSaved) {
        return;
      }
      lastRestoredQuestionsDataRef.current = questionsDataSaved;

      // Clear existing form arrays before repopulating
      Object.keys(wizardFormValue).forEach(key => {
        if (
          !getRestoreArrayType(
            wizardForm,
            key,
            formArraysKeys,
            wizardFormValue[key]
          )
        ) {
          return;
        }

        const control = getRestorableFormArray(wizardForm, key);
        while (control && control.length > 0) {
          control.removeAt(0);
        }
      });

      Object.keys(wizardFormValue).map(function (key) {
        const arrayType = getRestoreArrayType(
          wizardForm,
          key,
          formArraysKeys,
          wizardFormValue[key]
        );

        if (arrayType) {
          const control = getRestorableFormArray(wizardForm, key);
          const items = toRestorableArray(wizardFormValue[key], arrayType);

          items.forEach(item => {
            control?.push(
              arrayType === "object"
                ? FormBuilder.group(item)
                : FormBuilder.control(item)
            );
          });

          const template = defaultGroupTemplates[key];
          if (items.length === 0 && control && template) {
            for (let i = 0; i < template.rows; i++) {
              control.push(
                FormBuilder.group(
                  Object.fromEntries(template.fields.map(k => [k, [""]]))
                )
              );
            }
          }
        } else {
          if (!wizardForm.contains(key)) {
            wizardForm.addControl(
              key,
              FormBuilder.control(wizardFormValue[key])
            );
          } else {
            wizardForm.patchValue({ [key]: wizardFormValue[key] });
          }
        }
        return true;
      });
    }
    setRestoreVersion(v => v + 1);
  };

  const handleSubmit = createUnifiedSubmitHandler({
    wizardForm,
    qForm,
    studyId,
    sections: [WIZARD_SECTIONS.studyDesign],
    setAjaxLoading,
    setQuestionsDataSaved,
    questionsDataSaved,
    setFormFilledData,
    setWizardFormFilledData,
    setErrMsg,
    errRef,
    setIsSaving,
    questionsRef: null,
    CsrAPI,
    toast,
    isProduction,
    Sentry,
  });

  const PageContent = () => {
    return (
      <div className="questions-page">
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>
        <div className={"questions-content"}>
          <div className={"questions-content__items"}>
            {questionsData.current &&
              singleArm !== null &&
              multipleArm !== null &&
              doseFinding !== null && (
                <FieldGroup
                  control={wizardForm}
                  render={() => (
                    <StudyDesignQuestions
                      globalId={0}
                      wizardForm={wizardForm}
                      singleArm={singleArm}
                      multipleArm={multipleArm}
                      doseFinding={doseFinding}
                    />
                  )}
                />
              )}
          </div>
        </div>
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={
        "Answer the following questions related to Study Design & Demographics"
      }
      headerButton={false}
      pageContent={PageContent()}
      // prevButton={{
      //   buttonTitle: "Wizard Questions",
      //   buttonGoToAction: "/csr/" + studyId + "/questions",
      // }}
      prevButton={{
        buttonTitle: "Files",
        buttonGoToAction: "/csr/" + studyId + "/file-upload",
      }}
      nextButton={{
        buttonTitle: "Efficacy",
        buttonGoToAction: "/csr/" + studyId + "/efficacy-analysis",
      }}
      saveButtonHandler={handleSubmit}
      savePermission={PERMISSIONS.csrWizardsUpdate}
      saveButton={true}
    />
  );
};

export default StudyDesignDemographics;
